/* eslint-disable no-unused-vars */
/* eslint-disable no-empty */

import axios from 'axios'
import router from '@builder-web/Routes'
import { setStorage, getStorage, removeStorage } from '@builder-web/Utilities'

const SET_TOKEN = 'SET_TOKEN'
const SET_USER = 'SET_USER'
const LOGOUT = 'LOGOUT'
const SET_NOTIFICATIONS = 'SET_NOTIFICATIONS'
const SET_SETTINGS = 'SET_SETTINGS'
const READ_NOTIFICATION = 'READ_NOTIFICATION'
const ARCHIVED_NOTIFICATION = 'ARCHIVED_NOTIFICATION'
const SET_ARCHIVED_NOTIFICATIONS = 'SET_ARCHIVED_NOTIFICATIONS'

/**
 * Vuex state for auth module
 * @type {Object}
 */
const state = {
    user: null,
    token: getStorage('token') || null,
    notifications: [],
    archivedNotifications: [],
    authSettings: [],
}

/**
 * Vuex mutations for auth module
 * @type {Object}
 */
const mutations = {
    [SET_SETTINGS]: (state, payload) => {
        state.authSettings = payload
    },

    [ARCHIVED_NOTIFICATION]: (state, payload) => {
        state.notifications = state.notifications.filter((i) => i !== payload)
    },

    [SET_TOKEN]: (state, { token, expires = 365 }) => {
        state.token = token
        const expiryTime = parseInt(expires) * 86400000
        setStorage('token', token, expiryTime)
    },

    [SET_USER]: (state, payload) => {
        state.user = payload
    },

    [LOGOUT]: (state) => {
        removeStorage('token')
        removeStorage('impersonate')

        state.token = null
        state.user = null
    },

    [SET_NOTIFICATIONS]: (state, payload) => {
        state.notifications = payload
    },

    [SET_ARCHIVED_NOTIFICATIONS]: (state, payload) => {
        state.archivedNotifications = payload
    },

    [READ_NOTIFICATION]: (state, payload) => {
        const index = state.notifications.findIndex((e) => e.id === payload.id)
        state.notifications.splice(index, 1, payload)
    },
}

/**
 * Vuex actions for auth module
 * @type {Object}
 */
const actions = {
    async fetchSettings({ commit }) {
        const { data } = await axios.post('/api/settings')
        commit(SET_SETTINGS, data.settings)
    },

    async fetchNotifications({ commit }) {
        try {
            const { data } = await axios.get('/api/notifications')
            commit(SET_NOTIFICATIONS, data.data)
            return data
        } catch (e) {}
    },

    async readNotification({ commit }, id) {
        try {
            const { data } = await axios.patch(`/api/notifications/${id}/read`)
            commit(READ_NOTIFICATION, data.data)

            if (Object.prototype.hasOwnProperty.call(data.data, 'link')) {
                window.open(data.data.link, '_blank').focus()
            } else {
                if (
                    Object.prototype.hasOwnProperty.call(
                        data.data.router,
                        'web',
                    )
                ) {
                    router.push(data.data.router.web)
                } else {
                    router.push(data.data.router)
                }
            }

            return data
        } catch (e) {}
    },

    async archiveNotification({ commit }, item) {
        try {
            const { data } = await axios.patch(
                `/api/notifications/${item.id}/archive`,
            )
            commit(ARCHIVED_NOTIFICATION, item)
        } catch (e) {}
    },

    async fetchArchivedNotifications({ commit }) {
        try {
            const { data } = await axios.get('/api/notifications?archived=true')

            commit(SET_ARCHIVED_NOTIFICATIONS, data.data)
            return data.data
        } catch (e) {}
    },

    async readAllNotifications({ commit }) {
        try {
            const { data } = await axios.patch(`/api/notifications/read-all`)
            commit(SET_NOTIFICATIONS, data.data)
        } catch (e) {}
    },

    async fetchUser({ commit, state, dispatch }) {
        if (!state.token) {
            commit(LOGOUT)
            router.push({ name: 'login' })
            return
        }

        try {
            const { data } = await axios.get('/api/user')
            commit(SET_USER, data.data)
            return data
        } catch (e) {
            const response = e.response || e.original?.response
            const resData = response?.data
            const locked = resData && Object.prototype.hasOwnProperty.call(resData, 'locked') 
                ? resData.locked 
                : false

            dispatch('logout', { locked })
        }
    },

    async login({ dispatch }, payload) {
        const { data } = await axios.post('/api/login', payload)
        await dispatch('setToken', {
            token: data.token,
            expires: data.expires_after,
        })

        if (data.verification_token) {
            return {
                loggedIn: false,
                verificationToken: data.verification_token,
            }
        } else {
            return { loggedIn: true }
        }
    },

    async updateLastActivation() {
        return await axios.patch('/api/user/update-last-activation')
    },

    setToken({ commit }, payload) {
        commit(SET_TOKEN, payload)
    },

    async logout({ commit, state }) {
        if (state.token) {
            try {
                await axios.post('/api/logout', {}, { skipInterceptor: true })
            } catch {
                // swallow/skip on failure
            }
        }

        if (state.user) {
            window.Echo.private(
                process.env.VUE_APP_PUSHER_APP_CHANNEL,
            ).stopListening('.conversation.message.created.' + state.user.uuid)

            window.Echo.private(
                process.env.VUE_APP_PUSHER_APP_CHANNEL,
            ).stopListening(
                '.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
            )

            window.Echo.private(
                process.env.VUE_APP_PUSHER_APP_CHANNEL,
            ).stopListening('.refresh.user.' + state.user.uuid)
        }

        commit(LOGOUT)
        router.push({ name: 'login' })
    },
}

/**
 * Vuex getters for auth module
 * @type {Object}
 */
const getters = {
    token: (state) => state.token,
    user: (state) => state.user,
    notifications: (state) => state.notifications,
    archivedNotifications: (state) => state.archivedNotifications,
    unreadNotifications: (state) => state.notifications.filter((e) => e.unread),
    terms_and_conditions: (state) =>
        state.authSettings.length
            ? state.authSettings.find(
                  (setting) => setting.alias === 'terms_and_conditions',
              ).value
            : null,
    privacy_policy: (state) =>
        state.authSettings.length
            ? state.authSettings.find(
                  (setting) => setting.alias === 'privacy_policy',
              ).value
            : null,
}

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters,
}
